import identityClient from 'clients/identityClient';
import BlockingSpinnerOverlay from 'components/BlockingSpinnerOverlay';
import EmailField from 'components/fields/EmailField';
import { getCustomBrandingDetail } from 'helpers/customBranding';
import { buildSignUpSearchFromLocation } from 'helpers/buildSignUpSearchFromLocation';
import PropTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Alert, Button, Col, Form, Row } from 'reactstrap';
import { reduxForm } from 'redux-form';
import { error as err, registrationSteps, setGenericError } from '../';
import HpaStoreContext from '../../../../contexts/HpaStoreContext';
import errorMessages from '../../../../helpers/errorMessages';
import { WORKFLOW_TYPE_LINK_ACCOUNT } from 'helpers/registrationWorkflow';
import ErrorAlert from '../ErrorAlert';

const EmailValidation = ({ handleSubmit, navigate, touch, workflowType }) => {
    const context = useContext(HpaStoreContext);
    const [error, setError] = useState(err);
    const locationHook = useLocation();
    const history = useHistory();
    const prepopulatedEmail = locationHook.state?.email || '';
    const [email, setEmail] = useState(prepopulatedEmail);

    useEffect(() => {
        if (prepopulatedEmail === '') {
            setEmail('');
        }
    }, [prepopulatedEmail]);

    const [paramFirstName, setFirstName] = useState('');
    const [paramLastName, setLastName] = useState('');

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const fName = params.get('firstname');
        const lName = params.get('lastname');
        if (fName) {
            setFirstName(fName);
        }
        if (lName) {
            setLastName(lName);
        }
    }, []);

    const [isLoading, setIsLoading] = useState(false);
    const params = locationHook.search;
    const formEmail = useSelector(
        (state) => state.form?.emailValidation?.values?.email,
    );
    const signInSearch = buildSignUpSearchFromLocation(params, {
        username: formEmail || context.username,
        firstname: paramFirstName || undefined,
        lastname: paramLastName || undefined,
    });

    const RegistrationInstructionText = getCustomBrandingDetail(
        context,
        'registrationInstructionText',
    );

    const onContinue = async (email) => {
        if (isLoading) {
            return;
        }
        context.setData({ username: email });
        setIsLoading(true);
        try {
            const queryString = params ? `${params}` : '';
            const validationResponse = await identityClient.preRegistrationValidation(
                email,
                queryString,
                workflowType,
            );
            const { isProvisioned, isConfirmed, hStreamId, firstName, lastName, latestPersonId } =
                validationResponse?.data;
            const isNewUser = !isProvisioned;
            if (isNewUser && workflowType === WORKFLOW_TYPE_LINK_ACCOUNT) {
                history.push({
                    pathname: '/account/noMatchFound',
                    search: params,
                    state: {
                        username: email,
                        email,
                        workflowType,
                        firstName: firstName || paramFirstName,
                        lastName: lastName || paramLastName,
                    },
                });
            } else if (isNewUser) {
                await identityClient.sendRegistrationOTP(
                    email,
                    hStreamId,
                    undefined,
                    workflowType,
                );
                navigate(registrationSteps.VERIFICATIONCODE, {
                    email: email,
                    hStreamId: hStreamId,
                    firstName: firstName || paramFirstName,
                    lastName: lastName || paramLastName,
                });
            } else if (isProvisioned) {
                navigate(registrationSteps.CLAIMACCOUNT, {
                    email: email,
                    hStreamId: hStreamId,
                    firstName: firstName || paramFirstName || '',
                    lastName: lastName || paramLastName || '',
                    latestPersonId: latestPersonId,
                    isConfirmed: isConfirmed,
                    isProvisioned: true,
                });
            }
        } catch (e) {
            if (e.status === 409) {
                setError((_error) => ({
                    ..._error,
                    errorFound: true,
                    errorMessage: errorMessages.emailExists.message,
                    button: {
                        text: 'Sign In',
                        link: '/account/authorize',
                        id: 'registerLoginButton',
                    },
                }));
                if (
                    e.response &&
                    e.response.data &&
                    e.response.data.detail ===
                    'Multiple provisioned records or unconfirmed with UserProfiles are available'
                ) {
                    setError((_error) => ({
                        ..._error,
                        errorMessage: errorMessages.multipleProvisioned.message,
                        button: {
                            text: 'Need Help?',
                            link: '/needHelp',
                            id: 'needHelp',
                        },
                    }));
                }
            } else {
                setGenericError(setError);
            }
        } finally {
            setIsLoading(false);
        }
    };
    return (
        <>
            {RegistrationInstructionText && RegistrationInstructionText.value && RegistrationInstructionText.value.trim() !== '' && (
                <div className="mt-4">
                    <Alert color="info">{RegistrationInstructionText.value}</Alert>
                </div>
            )}
            <Form
                className="AuthWrapper__register mt-3"
                aria-busy={isLoading}
                onSubmit={handleSubmit((values) => {
                    if (isLoading) {
                        return;
                    }
                    const email = values?.email;
                    if (email) {
                        onContinue(email);
                    } else {
                        touch('email');
                        setError((_error) => ({
                            ..._error,
                            errorFound: false,
                        }));
                    }
                })}
            >
                <div className="position-relative">
                    <BlockingSpinnerOverlay
                        show={isLoading}
                        ariaLabel="Loading..."
                    />
                    {error.errorFound && <ErrorAlert error={error} />}
                    <EmailField
                        label="Email"
                        name="email"
                        disabled={isLoading}
                        busy={isLoading}
                    />
                    <div className="mt-2">
                        <Row>
                            <Col sm="4">
                                <Button
                                    color="primary"
                                    block
                                    disabled={isLoading}
                                    type="submit"
                                    id="validateEmail"
                                >
                                    Continue
                                </Button>
                            </Col>
                            <Col sm="8" className="d-flex align-items-center">
                                <div className="AuthWrapper__register-actions">
                                    Already have an hStream ID?
                                    <Link
                                        to={{
                                            pathname: '/account/authorize',
                                            search: signInSearch,
                                        }}
                                        tabIndex={isLoading ? -1 : 0}
                                        aria-disabled={isLoading || undefined}
                                        onClick={(e) => {
                                            if (isLoading) {
                                                e.preventDefault();
                                            }
                                        }}
                                    >
                                        Sign In
                                    </Link>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </div>
            </Form>
        </>
    );
};

EmailValidation.propTypes = {
    toSignIn: PropTypes.string.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    navigate: PropTypes.func.isRequired,
};

export default reduxForm({
    form: 'emailValidation',
})(EmailValidation);

export { EmailValidation as EmailValidationForm };
